/**
 * The dictation Engine menu that Settings › Voice and onboarding share: the
 * platform recogniser, then one entry per local dictation model. Choosing a
 * model picks the local engine with that model, and downloads the model when
 * it is not in yet. One model is marked Recommended for the languages the
 * user speaks, by the rule Rust owns (`dictation_model_rule.rs`).
 *
 * Each model shows how well it hears the languages the user speaks
 * (`dictation-model-accuracy.ts`). Until a chosen model is installed,
 * Standard listens where it is in and the system recogniser otherwise
 * (`dictation_models.rs`); dictation switches over by itself once the
 * download is in. A failed download keeps the choice, which startup prepares
 * again, and offers a retry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SelectFieldOption } from "@/components/ui";
import { useT } from "@/i18n";
import {
  DICTATION_MODEL_NAME_KEY,
  settingsVoiceMessages,
} from "@/i18n/messages/settings-voice";
import { useEffectiveAppLanguage } from "@/stores/app-language-store";
import {
  spokenLanguagesOrDefault,
  useDictationSettingsStore,
} from "@/stores/dictation-settings-store";

import {
  dictationEngineOptions,
  engineMenuOption,
  type DictationEngineChoice,
} from "./dictation-engine-options";
import { accuracyLine } from "./dictation-model-accuracy";
import {
  dictationModelForLanguages,
  type DictationModelId,
} from "./dictation-models";
import { useDictationEngineChoice } from "./use-dictation-engine-choice";
import {
  useDictationModels,
  type DictationModelsView,
} from "./use-dictation-models";
import { megabytes } from "./voice-assets";

const LOCAL_ENGINE: DictationEngineChoice = "whisper";

export type SpareDictationModel = {
  id: DictationModelId;
  name: string;
  megabytes: number;
};

export type DictationEngineMenu = {
  options: SelectFieldOption[];
  /** The option in effect: the platform recogniser, or the chosen model. */
  value: string;
  engine: DictationEngineChoice;
  /** Whether the engine in effect is known (`useDictationEngineChoice`). */
  resolved: boolean;
  isSelectable: boolean;
  /** One line on what the option in effect is for. */
  hint: string | undefined;
  choose: (value: string) => void;
  /** The chosen model's download, while the local engine waits for it. */
  download: { name: string; percent: number } | null;
  /** The chosen model, when its last download failed. */
  failed: string | null;
  retry: () => void;
  /** Why the last choice or removal did not happen. */
  error: string | null;
  /** Downloaded models that are not the choice, which can be removed. */
  spare: SpareDictationModel[];
  remove: (model: DictationModelId) => Promise<void>;
};

/** The languages the user speaks, as Settings shows them. */
function useSpokenLanguages(): string[] {
  const appLanguage = useEffectiveAppLanguage();
  const saved = useDictationSettingsStore((state) => state.spokenLanguages);
  return useMemo(
    () => spokenLanguagesOrDefault(saved, appLanguage),
    [saved, appLanguage],
  );
}

/** The model the rule picks for `languages`; the last answer while a new one
 *  is on its way. */
export function useSuggestedDictationModel(
  languages: readonly string[],
): DictationModelId | null {
  const [recommended, setRecommended] = useState<DictationModelId | null>(null);
  useEffect(() => {
    let cancelled = false;
    void dictationModelForLanguages(languages).then(
      (model) => {
        if (!cancelled) setRecommended(model);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [languages]);
  return recommended;
}

/** A model's name and one line on what it is for, as Settings names it. */
export function useDictationModelNames() {
  const t = useT(settingsVoiceMessages);
  const nameOf = useCallback(
    (id: DictationModelId) => {
      const key = DICTATION_MODEL_NAME_KEY[id]?.name;
      return key ? t(key) : id;
    },
    [t],
  );
  const lineOf = useCallback(
    (id: DictationModelId) => {
      const key = DICTATION_MODEL_NAME_KEY[id]?.line;
      return key ? t(key) : undefined;
    },
    [t],
  );
  return { nameOf, lineOf };
}

/** The chosen model while the local engine waits for it: how far its
 *  download is, or that the last one failed. */
export function chosenModelDownload(
  dictation: Pick<DictationModelsView, "models" | "percent" | "failed">,
  engine: DictationEngineChoice,
): { id: DictationModelId; percent: number | null; failed: boolean } | null {
  const models = dictation.models;
  const selected = models?.models.find((model) => model.id === models.selected);
  if (engine !== LOCAL_ENGINE || !selected || selected.state === "installed")
    return null;
  const percent =
    dictation.percent[selected.id] ??
    (selected.state === "downloading" ? 0 : null);
  return {
    id: selected.id,
    percent,
    failed: percent === null && dictation.failed.includes(selected.id),
  };
}

export function useDictationEngineMenu(settings?: {
  onChoose?: (engine: DictationEngineChoice) => void;
  /** The platform whose recogniser to offer; this machine's by default. */
  windows?: boolean;
}): DictationEngineMenu {
  const t = useT(settingsVoiceMessages);
  const engineChoice = useDictationEngineChoice(settings);
  const chooseEngine = engineChoice.choose;
  const chosenEngine = engineChoice.chosen;
  const dictation = useDictationModels();
  const chooseModel = dictation.choose;
  const languages = useSpokenLanguages();
  const recommended = useSuggestedDictationModel(languages);
  const models = dictation.models;
  const { nameOf, lineOf } = useDictationModelNames();

  const engines = dictationEngineOptions(settings?.windows);
  const platform = engines
    .filter((option) => option.id !== LOCAL_ENGINE)
    .map((option) => ({
      ...engineMenuOption(option),
      detail: t("modelAccuracyNotMeasured"),
    }));
  // Until the models are known - or where there are none - the local engine
  // is one entry, as it is to the platform.
  const local = models
    ? models.models.map((model) => {
        const line = lineOf(model.id);
        const size = t("modelDownloadSize", {
          size: megabytes(model.missing_bytes || model.total_bytes),
        });
        return {
          value: model.id,
          label: nameOf(model.id),
          description:
            model.state === "installed"
              ? line
              : [line, size].filter(Boolean).join(" · "),
          detail: accuracyLine(model, languages),
          trailing:
            model.id === recommended ? t("modelRecommended") : undefined,
        };
      })
    : engines
        .filter((option) => option.id === LOCAL_ENGINE)
        .map(engineMenuOption);
  const options: SelectFieldOption[] = [...platform, ...local];

  const engine = engineChoice.engine;
  const value =
    engine === LOCAL_ENGINE ? (models?.selected ?? LOCAL_ENGINE) : engine;
  const hint =
    engine === LOCAL_ENGINE && models
      ? lineOf(models.selected)
      : options.find((option) => option.value === value)?.description;

  // Each pick, so a model choice that answers late never switches the engine
  // over a later pick.
  const latestChoice = useRef(0);
  const choose = useCallback(
    (next: string) => {
      const choice = ++latestChoice.current;
      if (next === "system" || !models) {
        chooseEngine(next as DictationEngineChoice);
        return;
      }
      // The model becomes the choice before the engine changes, so the local
      // engine never prepares any other model meanwhile. Moving between
      // models leaves the engine choice as it is.
      void chooseModel(next).then((chosen) => {
        if (
          chosen &&
          choice === latestChoice.current &&
          chosenEngine !== LOCAL_ENGINE
        )
          chooseEngine(LOCAL_ENGINE);
      });
    },
    [chooseEngine, chooseModel, chosenEngine, models],
  );

  const waiting = chosenModelDownload(dictation, engine);
  const download =
    waiting && waiting.percent !== null
      ? { name: nameOf(waiting.id), percent: waiting.percent }
      : null;
  const failed = waiting?.failed ? nameOf(waiting.id) : null;
  const retry = useCallback(() => {
    if (models) void chooseModel(models.selected);
  }, [chooseModel, models]);

  const spare = (models?.models ?? [])
    .filter(
      (model) =>
        !model.builtin &&
        model.state === "installed" &&
        model.id !== models?.selected &&
        model.id !== models?.active,
    )
    .map((model) => ({
      id: model.id,
      name: nameOf(model.id),
      megabytes: megabytes(model.total_bytes),
    }));

  return {
    options,
    value,
    engine,
    resolved: engineChoice.resolved,
    isSelectable: engineChoice.isSelectable,
    hint,
    choose,
    download,
    failed,
    retry,
    error: dictation.error,
    spare,
    remove: dictation.remove,
  };
}
